import { useMemo } from 'react';
import { isUserPromptEvent } from '@reflex/shared';
import type { Agent, AgentActivity, AxonEvent } from '@/lib/types';
import { parseEventPayload } from '@reflex/ui/client/event-utils';
import { activityFromSignal, assertNever, isSignalType } from './agent-activity-utils';

function hasUnfinishedTurn(events: readonly AxonEvent[]): boolean {
  let latestStart = -1;
  let latestTerminal = -1;

  for (let i = 0; i < events.length; i += 1) {
    const event = events[i];
    if (event.type === 'turn.started' || event.type === 'turn.resumed') {
      latestStart = i;
      continue;
    }

    if (
      event.type === 'turn.completed' ||
      event.type === 'turn.failed' ||
      event.type === 'turn.cancelled'
    ) {
      latestTerminal = i;
      continue;
    }

    // ACP's native terminal is the response to session/prompt. The broker
    // normally follows it with turn.completed/cancelled, but recognizing it
    // here avoids leaving the composer in its working state if that derived
    // event is delayed or absent. Both directions share this event type, so
    // it is classified by payload here and never by the prompt check below.
    if (event.type === 'session/prompt') {
      const payload = parseEventPayload<{
        prompt?: unknown;
        stopReason?: unknown;
        stop_reason?: unknown;
      }>(event.payload);
      if (typeof payload?.stopReason === 'string' || typeof payload?.stop_reason === 'string') {
        latestTerminal = i;
      } else if (Array.isArray(payload?.prompt)) {
        latestStart = i;
      }
      continue;
    }

    // A durable user prompt opens a turn just as much as `turn.started` does.
    // The broker only publishes `turn.started` once the agent process picks the
    // prompt up, which is minutes away when the message had to wake a suspended
    // devbox — the whole time, the prompt is already on the stream.
    if (isUserPromptEvent(event)) latestStart = i;
  }

  return latestStart > latestTerminal;
}

/** Whether any durable user prompt sits after `index` in the stream. */
function hasPromptAfter(events: readonly AxonEvent[], index: number): boolean {
  for (let i = index + 1; i < events.length; i += 1) {
    if (isUserPromptEvent(events[i])) return true;
  }
  return false;
}

/** Turn activity controls interrupt and dispatch; background work does not open a turn. */
export function deriveTurnActivity(agent: Agent, events: AxonEvent[] | undefined): AgentActivity {
  switch (agent.status) {
    case 'starting':
      return 'starting';
    case 'completed':
      return 'completed';
    case 'stopping':
    case 'stopped':
    case 'terminated':
      return 'stopped';
    case 'error':
      return 'error';
    case 'running':
    case 'needs_input':
    case 'interrupted':
      break;
    default:
      assertNever(agent.status);
  }

  if (!events || events.length === 0) {
    if (agent.status === 'interrupted') return 'interrupted';
    if (agent.status === 'needs_input') return 'waiting_input';
    if (agent.hasBackgroundWork && agent.turnState !== 'working') return 'idle';
    return 'working';
  }

  // `agent.interrupted` is written optimistically after the cancellation
  // request is sent. ACP cancellation is asynchronous, so retain the
  // working composer whenever the stream still has an unmatched turn start.
  // This prevents a failed cancellation from sending a new prompt directly
  // into a live turn (and keeps Queue + Interrupt available instead).
  if (agent.status === 'interrupted' && hasUnfinishedTurn(events)) {
    return 'working';
  }
  if (agent.status === 'interrupted') return 'interrupted';

  // Scan backwards for the most recent signal event. tool_use events are
  // informational and can arrive after state-determining events like
  // need_input (because the hook reports the Bash command used to publish
  // it). Treat tool_use as a low-priority fallback and keep scanning.
  let fallback: AgentActivity | null = null;

  for (let i = events.length - 1; i >= 0; i--) {
    const evtType = events[i].type;
    if (evtType === 'background.changed') {
      // A background snapshot is between-turn evidence, not a replacement
      // for a later start/end boundary. Keep scanning for the actual turn.
      fallback ??= 'idle';
      continue;
    }
    if (!isSignalType(evtType)) continue;

    const result = activityFromSignal(evtType, events[i].payload);
    if (result === null) break;

    if (evtType === 'agent.tool_use') {
      fallback ??= result;
      continue;
    }

    // The stream can still contain `agent.need_input` after the durable row
    // has already returned to `running` (e.g. AskUserQuestion answered via
    // control_response + tool_result without a fresh `turn.started`). Trust
    // `agent.status` over that stale marker and keep scanning older signals.
    if (evtType === 'agent.need_input' && agent.status !== 'needs_input') {
      continue;
    }

    // The newest signal ended a turn, but a prompt has landed since: the next
    // turn has been asked for and its `turn.started` has not arrived yet
    // (waking a suspended devbox takes it minutes). Report that as working, so
    // the composer offers Interrupt and queues the next message behind the turn
    // instead of dispatching a second one on top of it.
    if (result === 'idle' && hasPromptAfter(events, i)) return 'working';

    return result;
  }

  if (fallback === 'idle') {
    if (hasUnfinishedTurn(events)) return 'working';
    if (agent.status === 'needs_input') return 'waiting_input';
  }
  if (fallback !== null) return fallback;
  if (agent.status === 'needs_input') return 'waiting_input';
  return 'working';
}

export function deriveActivity(agent: Agent, events: AxonEvent[] | undefined): AgentActivity {
  const turnActivity = deriveTurnActivity(agent, events);
  if (agent.status !== 'running' || turnActivity !== 'idle') return turnActivity;

  // Background membership survives ordinary turn boundaries. Prefer the
  // loaded stream's latest snapshot; the durable row covers a paged-out one.
  let hasBackgroundWork = agent.hasBackgroundWork === true;
  const streamEvents = events ?? [];
  for (let i = streamEvents.length - 1; i >= 0; i--) {
    const event = streamEvents[i];
    if (
      event.type === 'devbox.suspended' ||
      event.type === 'devbox.shutdown' ||
      event.type === 'devbox.failed'
    ) {
      hasBackgroundWork = false;
      break;
    }
    if (event.type === 'background.changed') {
      const payload = parseEventPayload<{ active?: unknown }>(event.payload);
      if (!Array.isArray(payload?.active)) continue;
      hasBackgroundWork = payload.active.length > 0;
      break;
    }
  }
  return hasBackgroundWork ? 'working' : turnActivity;
}

export function useAgentActivityFromStream(
  agent: Agent,
  events: AxonEvent[] | undefined,
): AgentActivity {
  return useMemo(() => deriveActivity(agent, events), [agent, events]);
}
